import searchSession from '@/js/common/Domain/Service/search/searchSession';
import { COUNTRY_PAGE_TYPE, isLoadMoreEnabled } from '@/js/common/Domain/Service/search/serpPagination';
import SerpTrackingService, {
    type CountryDeepClickTarget,
    type CountryListingMode,
    type LoadMoreErrorReason,
} from '@ve/pages/serp/analytics/yandexAnalytics/serpTrackingService';
import type { ExWindow } from '@ve/types';

declare let window: ExWindow;

const serpTrackingService = new SerpTrackingService();

const LOAD_MORE_BUTTON_SELECTOR = '.js-load-more';
const HOTEL_CARD_SELECTOR = '.serpHotelCard__container';
const HOTEL_FEED_SELECTOR = '#hotelTemplate';

const TOUR_LINK_SELECTOR = '.serpHotelCard__right-part-wrapper';

const DEEP_CLICK_RULES: Array<{ selector: string; target: CountryDeepClickTarget }> = [
    { selector: TOUR_LINK_SELECTOR, target: 'tour' },
    { selector: `${HOTEL_CARD_SELECTOR} a, .goToHotel`, target: 'hotel' },
    { selector: 'a[href*="/resorts"]', target: 'resort' },
    { selector: '#startSearch, .js-scroll-to-top', target: 'search' },
];

let isLoadMoreShownTracked = false;
let isTrackingInitialized = false;

function isCountryPage(): boolean {
    return window.appConfig?.pageDataType === COUNTRY_PAGE_TYPE;
}

function getCountrySlug(): string {
    return window.location.pathname.split('/').find(Boolean) || '';
}

const EMPTY_SEARCH_STATE_ID = 'none';

function getSearchStateId(): string {
    return String(searchSession.getSearchId() || '') || EMPTY_SEARCH_STATE_ID;
}

function getListingMode(): CountryListingMode {
    return isLoadMoreEnabled() ? 'button' : 'scroll';
}

function getCardPosition(clickedElement: Element): number {
    const card = clickedElement.closest(HOTEL_CARD_SELECTOR);

    if (!card) {
        return 0;
    }

    const feedCards = Array.from(document.querySelectorAll(`${HOTEL_FEED_SELECTOR} ${HOTEL_CARD_SELECTOR}`));

    return feedCards.indexOf(card) + 1;
}

export function trackCountryPageView(): void {
    if (!isCountryPage()) {
        return;
    }

    serpTrackingService.countryPageView(getCountrySlug(), getListingMode());
}

export function trackLoadMoreShown(): void {
    if (!isCountryPage() || isLoadMoreShownTracked) {
        return;
    }

    isLoadMoreShownTracked = true;
    serpTrackingService.countryLoadMoreShown(getCountrySlug(), getSearchStateId());
}

export function trackLoadMoreClick(toursLoaded: number): void {
    if (!isCountryPage()) {
        return;
    }

    serpTrackingService.countryLoadMoreClick(getCountrySlug(), getSearchStateId(), toursLoaded);
}

export function trackCountryDeepClick(target: CountryDeepClickTarget, position = 0): void {
    if (!isCountryPage()) {
        return;
    }

    serpTrackingService.countryDeepClick(getCountrySlug(), getSearchStateId(), target, position);
}

export function trackLoadMoreError(reason: LoadMoreErrorReason): void {
    if (!isCountryPage()) {
        return;
    }

    serpTrackingService.countryLoadMoreError(getCountrySlug(), getSearchStateId(), reason);
}

function observeLoadMoreButton(): void {
    const button = document.querySelector(LOAD_MORE_BUTTON_SELECTOR);

    if (!button || typeof IntersectionObserver === 'undefined') {
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
            trackLoadMoreShown();
            observer.disconnect();
        }
    });

    observer.observe(button);
}

export function initCountrySeoTracking(): void {
    if (!isCountryPage() || isTrackingInitialized) {
        return;
    }

    isTrackingInitialized = true;
    trackCountryPageView();
    observeLoadMoreButton();

    document.addEventListener('pointerdown', (event) => {
        const clickedElement = event.target as Element | null;

        if (!clickedElement?.closest) {
            return;
        }

        const matchedRule = DEEP_CLICK_RULES.find(({ selector }) => clickedElement.closest(selector));

        if (matchedRule) {
            trackCountryDeepClick(matchedRule.target, getCardPosition(clickedElement));
        }
    }, true);
}
